import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, List, ListItemButton, ListItemText, Typography, Alert, Divider } from '@mui/material';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import { MarkdownRenderer } from '../_components/MarkdownRenderer';
import { neutral, primary } from '_lex/_foundations/phxColors';
import { articleContentMap, articlesMetadata } from '../_articles';

interface Article {
  id: string;
  title: string;
  filename: string;
  order: number;
  filePatterns: string[];
}

interface Category {
  id: string;
  title: string;
  order: number;
  articles: Article[];
}

const KBList: React.FC = () => {
  const { articleId } = useParams<{ articleId: string }>();
  const navigate = useNavigate();

  // Sort categories and their articles
  const categories = useMemo(() => {
    return [...articlesMetadata.categories]
      .sort((a, b) => a.order - b.order)
      .map((category) => ({
        ...category,
        articles: [...category.articles].sort((a, b) => a.order - b.order),
      }));
  }, []);

  // Get all articles flattened
  const allArticles = useMemo(() => {
    return categories.flatMap((category) => category.articles);
  }, [categories]);

  const currentArticle = useMemo(() => {
    return allArticles.find((article) => article.id === articleId);
  }, [allArticles, articleId]);

  // Get article content from the static imports
  const articleContent = useMemo(() => {
    if (!articleId) return '';
    return articleContentMap[articleId] || '';
  }, [articleId]);

  const error = useMemo(() => {
    if (articleId && !articleContent && currentArticle) {
      return 'Article content not found';
    }
    if (articleId && !currentArticle) {
      return 'Article not found';
    }
    return null;
  }, [articleId, articleContent, currentArticle]);

  const handleArticleClick = (article: Article) => {
    navigate(`/admin/kb/${article.id}`);
  };

  // Left drawer with categorized article list
  const leftDrawer = (
    <Box
      sx={{
        width: 280,
        height: '100%',
        borderRight: `1px solid ${neutral[200]}`,
        backgroundColor: neutral[25],
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ p: 3, borderBottom: `1px solid ${neutral[200]}` }}>
        <Typography
          variant='h6'
          sx={{ fontWeight: 600, color: neutral[900] }}
        >
          Articles
        </Typography>
      </Box>
      <Box sx={{ py: 1, overflow: 'auto', flex: 1 }}>
        {categories.map((category, categoryIndex) => (
          <Box key={category.id}>
            {/* Category Header */}
            <Box sx={{ px: 2, py: 1.5, mt: categoryIndex > 0 ? 2 : 0 }}>
              <Typography
                variant='caption'
                sx={{
                  fontWeight: 600,
                  color: neutral[600],
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {category.title}
              </Typography>
            </Box>

            {/* Category Articles */}
            <List sx={{ py: 0 }}>
              {category.articles.map((article) => (
                <ListItemButton
                  key={article.id}
                  selected={articleId === article.id}
                  onClick={() => handleArticleClick(article)}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: primary[50],
                      borderLeft: `3px solid ${primary[600]}`,
                      '&:hover': {
                        backgroundColor: primary[100],
                      },
                    },
                    '&:hover': {
                      backgroundColor: neutral[50],
                    },
                  }}
                >
                  <ListItemText
                    primary={article.title}
                    primaryTypographyProps={{
                      fontWeight: articleId === article.id ? 600 : 400,
                      color: articleId === article.id ? primary[700] : neutral[700],
                    }}
                  />
                </ListItemButton>
              ))}
            </List>

            {/* Divider between categories (except last) */}
            {categoryIndex < categories.length - 1 && <Divider sx={{ my: 1, mx: 2 }} />}
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <TakeoverLayout
      backUrl='/dashboard'
      pageTitle='Admin Portal Knowledge Base'
      leftDrawer={leftDrawer}
      contentBackground='white'
    >
      <Box
        sx={{
          maxWidth: 900,
          mx: 'auto',
          py: 4,
        }}
      >
        {error && (
          <Alert
            severity='error'
            sx={{ mb: 2 }}
          >
            {error}
          </Alert>
        )}
        {!error && articleContent && <MarkdownRenderer content={articleContent} />}
        {!error && !articleContent && !currentArticle && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography
              variant='h6'
              sx={{ color: neutral[600] }}
            >
              Select an article to view
            </Typography>
          </Box>
        )}
      </Box>
    </TakeoverLayout>
  );
};

export default KBList;
